// src/components/sections/InfrastructureSection.js
import React, { useState } from 'react';
import { Container, Row, Col, Tab, Nav } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
    FaHospital, FaProcedures, FaBaby, FaMicroscope,
    FaBed, FaShieldAlt, FaThermometerHalf, FaUsers
} from 'react-icons/fa';

const InfrastructureSection = () => {
    const [key, setKey] = useState('operating');

    const facilities = [
        {
            id: 'operating',
            title: 'Operation Theatres',
            icon: <FaProcedures />,
            features: [
                'Advanced Modular OTs',
                'Laminar Air Flow System',
                'Digital Integration',
                'Robotic Surgery Setup'
            ],
            description: 'State-of-the-art operating theatres with advanced life support systems',
            imageColor: '#20B2AA'
        },
        {
            id: 'delivery',
            title: 'Labor & Delivery',
            icon: <FaBaby />,
            features: [
                'Private Labor Suites',
                'Water Birth Options',
                '24/7 Anesthesia',
                'Family-centered Care'
            ],
            description: 'Comfortable, private suites for natural and assisted deliveries',
            imageColor: '#FF7F50'
        },
        {
            id: 'nicu',
            title: 'NICU / PICU',
            icon: <FaHospital />,
            features: [
                'Level III NICU',
                'Ventilator Support',
                'Kangaroo Care Rooms',
                'Parent Accommodation'
            ],
            description: 'Advanced neonatal and pediatric intensive care units',
            imageColor: '#87CEEB'
        },
        {
            id: 'labs',
            title: 'Diagnostic Labs',
            icon: <FaMicroscope />,
            features: [
                'Advanced Imaging',
                'Genetic Testing',
                'Rapid Results',
                'Digital Pathology'
            ],
            description: 'Comprehensive diagnostic and imaging services',
            imageColor: '#9370DB'
        },
        {
            id: 'patient',
            title: 'Patient Rooms',
            icon: <FaBed />,
            features: [
                'Private Suites',
                'Mother-baby Rooms',
                'Family Stay Options',
                'Entertainment Systems'
            ],
            description: 'Comfortable accommodation for patients and families',
            imageColor: '#3CB371'
        },
        {
            id: 'safety',
            title: 'Safety Standards',
            icon: <FaShieldAlt />,
            features: [
                'Infection Control',
                'Fire Safety Systems',
                'Emergency Power',
                'Quality Audits'
            ],
            description: 'Stringent safety protocols and quality assurance',
            imageColor: '#FFD700'
        }
    ];

    return (
        <section id="facilities" className="section-padding">
            <Container>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-5"
                >
                    <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                        Advanced Infrastructure & Facilities
                    </h2>
                    <p className="lead" style={{ color: '#666', maxWidth: '700px', margin: '0 auto' }}>
                        Modern healthcare infrastructure designed for safety, comfort, and optimal medical outcomes
                    </p>
                </motion.div>

                <Tab.Container activeKey={key} onSelect={(k) => setKey(k)}>
                    <Row>
                        <Col lg={3} className="mb-4 mb-lg-0">
                            <Nav variant="pills" className="flex-lg-column">
                                {facilities.map((facility) => (
                                    <Nav.Item key={facility.id} className="mb-2">
                                        <Nav.Link
                                            eventKey={facility.id}
                                            className="d-flex align-items-center p-3 healthcare-card border-0"
                                            style={{
                                                backgroundColor: key === facility.id ? `${facility.imageColor}15` : 'white',
                                                borderLeft: key === facility.id ? `4px solid ${facility.imageColor}` : 'none'
                                            }}
                                        >
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    backgroundColor: key === facility.id ? facility.imageColor : `${facility.imageColor}20`,
                                                    color: key === facility.id ? 'white' : facility.imageColor,
                                                    fontSize: '1.3rem',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {facility.icon}
                                            </div>
                                            <span
                                                className="fw-medium"
                                                style={{ color: key === facility.id ? 'var(--dark-teal)' : '#666' }}
                                            >
                                                {facility.title}
                                            </span>
                                        </Nav.Link>
                                    </Nav.Item>
                                ))}
                            </Nav>
                        </Col>

                        <Col lg={9}>
                            <Tab.Content>
                                {facilities.map((facility) => (
                                    <Tab.Pane key={facility.id} eventKey={facility.id}>
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            <div className="healthcare-card border-0 p-4 h-100">
                                                <div className="d-flex align-items-center mb-4">
                                                    <div
                                                        className="rounded-circle d-flex align-items-center justify-content-center me-4"
                                                        style={{
                                                            width: '80px',
                                                            height: '80px',
                                                            backgroundColor: `${facility.imageColor}20`,
                                                            color: facility.imageColor,
                                                            fontSize: '2rem'
                                                        }}
                                                    >
                                                        {facility.icon}
                                                    </div>
                                                    <div>
                                                        <h3 className="fw-bold mb-2" style={{ color: 'var(--dark-teal)' }}>
                                                            {facility.title}
                                                        </h3>
                                                        <p className="mb-0" style={{ color: facility.imageColor, fontWeight: '500' }}>
                                                            {facility.description}
                                                        </p>
                                                    </div>
                                                </div>

                                                <Row className="g-4">
                                                    <Col md={6}>
                                                        <h5 className="fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                                                            Key Features
                                                        </h5>
                                                        <ul className="list-unstyled">
                                                            {facility.features.map((feature, index) => (
                                                                <li key={index} className="mb-3 d-flex align-items-start">
                                                                    <div
                                                                        className="rounded-circle d-flex align-items-center justify-content-center me-3 mt-1"
                                                                        style={{
                                                                            width: '24px',
                                                                            height: '24px',
                                                                            backgroundColor: `${facility.imageColor}20`,
                                                                            color: facility.imageColor,
                                                                            fontSize: '0.7rem',
                                                                            flexShrink: 0
                                                                        }}
                                                                    >
                                                                        <FaThermometerHalf />
                                                                    </div>
                                                                    <span style={{ color: '#666' }}>{feature}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </Col>

                                                    <Col md={6}>
                                                        <div
                                                            className="rounded overflow-hidden h-100"
                                                            style={{
                                                                backgroundColor: `${facility.imageColor}10`,
                                                                border: `2px dashed ${facility.imageColor}30`
                                                            }}
                                                        >
                                                            <div className="p-4 h-100 d-flex flex-column justify-content-center">
                                                                <div className="text-center mb-3">
                                                                    <FaUsers size={40} style={{ color: facility.imageColor, opacity: 0.5 }} />
                                                                </div>
                                                                <h5 className="fw-bold text-center mb-3" style={{ color: facility.imageColor }}>
                                                                    Patient-Centric Design
                                                                </h5>
                                                                <p className="text-center mb-0" style={{ color: '#666' }}>
                                                                    Designed with patient comfort, safety, and optimal medical outcomes as primary considerations
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <div className="mt-4 pt-4 border-top">
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <div className="text-center p-3 rounded" style={{ backgroundColor: `${facility.imageColor}10` }}>
                                                                <h6 className="fw-bold mb-2" style={{ color: facility.imageColor }}>
                                                                    Capacity
                                                                </h6>
                                                                <p className="mb-0" style={{ color: '#666' }}>
                                                                    {facility.id === 'nicu' ? '30 beds' :
                                                                        facility.id === 'operating' ? '8 theatres' :
                                                                            facility.id === 'patient' ? '150 rooms' : '24/7 operation'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="text-center p-3 rounded" style={{ backgroundColor: `${facility.imageColor}10` }}>
                                                                <h6 className="fw-bold mb-2" style={{ color: facility.imageColor }}>
                                                                    Availability
                                                                </h6>
                                                                <p className="mb-0" style={{ color: '#666' }}>
                                                                    {['nicu', 'operating'].includes(facility.id) ? '24/7' : 'Scheduled & Emergency'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="text-center p-3 rounded" style={{ backgroundColor: `${facility.imageColor}10` }}>
                                                                <h6 className="fw-bold mb-2" style={{ color: facility.imageColor }}>
                                                                    Staff
                                                                </h6>
                                                                <p className="mb-0" style={{ color: '#666' }}>
                                                                    {facility.id === 'nicu' ? 'Specialist Team' : 'Trained Professionals'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </Tab.Pane>
                                ))}
                            </Tab.Content>
                        </Col>
                    </Row>
                </Tab.Container>

                {/* Safety Standards Banner */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="mt-5"
                >
                    <div
                        className="p-5 rounded text-white"
                        style={{
                            background: 'linear-gradient(135deg, var(--dark-teal) 0%, var(--primary-teal) 100%)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <div className="position-absolute top-0 end-0 opacity-25">
                            <FaShieldAlt size={150} />
                        </div>

                        <Row className="align-items-center position-relative" style={{ zIndex: 1 }}>
                            <Col lg={8}>
                                <h3 className="fw-bold mb-3">Highest Safety Standards</h3>
                                <p className="mb-0 opacity-90">
                                    Our facilities adhere to international healthcare standards with regular audits,
                                    infection control protocols, and continuous staff training to ensure the safest
                                    environment for patients and staff.
                                </p>
                            </Col>
                            <Col lg={4} className="text-lg-end mt-4 mt-lg-0">
                                <div className="d-inline-block p-3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                                    <h4 className="fw-bold mb-0">99.8%</h4>
                                    <small>Safety Compliance Rate</small>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </motion.div>
            </Container>
        </section>
    );
};

export default InfrastructureSection;